import React, { useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const SuspensionControlPanel = ({ tenant, onClose, onSuccess }) => {
  const { user } = useAuth();
  const [suspensionReason, setSuspensionReason] = useState('non_payment');
  const [suspensionNotes, setSuspensionNotes] = useState('');
  const [notifyMembers, setNotifyMembers] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const suspensionReasons = [
    { value: 'non_payment', label: 'Non-Payment of Bills', icon: 'DollarSign' },
    { value: 'policy_violation', label: 'Policy Violation', icon: 'AlertTriangle' },
    { value: 'fraud', label: 'Fraudulent Activity', icon: 'Shield' },
    { value: 'voluntary', label: 'Voluntary Suspension', icon: 'Pause' },
    { value: 'other', label: 'Other Reason', icon: 'HelpCircle' }
  ];

  const handleSuspend = async () => {
    if (!suspensionNotes?.trim()) {
      setError('Please provide suspension notes');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Update tenant status to suspended
      const { error: updateError } = await supabase
        ?.from('tenants')
        ?.update({ 
          status: 'suspended',
          updated_at: new Date()?.toISOString()
        })
        ?.eq('id', tenant?.id);

      if (updateError) throw updateError;

      // Create suspension record
      const { error: suspensionError } = await supabase
        ?.from('tenant_suspensions')
        ?.insert({
          tenant_id: tenant?.id,
          suspended_by: user?.id,
          suspension_reason: suspensionReason,
          suspension_notes: suspensionNotes,
          is_active: true
        });

      if (suspensionError) throw suspensionError;

      // Create notification for gym owner
      const { error: notificationError } = await supabase
        ?.from('notifications')
        ?.insert({
          tenant_id: tenant?.id,
          user_id: tenant?.owner_id,
          title: 'Gym Location Suspended',
          message: `Your gym location has been suspended. Reason: ${suspensionReasons?.find(r => r?.value === suspensionReason)?.label}. ${suspensionNotes}`,
          type: 'system',
          priority: 'high'
        });

      if (notificationError) throw notificationError;

      // Optionally notify all members
      if (notifyMembers) {
        const { data: members } = await supabase
          ?.from('tenant_members')
          ?.select('user_id')
          ?.eq('tenant_id', tenant?.id)
          ?.eq('is_active', true);

        if (members?.length > 0) {
          const memberNotifications = members?.map(member => ({
            tenant_id: tenant?.id,
            user_id: member?.user_id,
            title: 'Gym Location Temporarily Unavailable',
            message: `${tenant?.name} is temporarily unavailable. We apologize for any inconvenience.`,
            type: 'system',
            priority: 'medium'
          }));

          await supabase?.from('notifications')?.insert(memberNotifications);
        }
      }

      onSuccess();
    } catch (err) {
      setError(err?.message || 'Failed to suspend tenant');
      console.error('Suspension error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-heading font-bold text-foreground flex items-center gap-2">
              <Icon name="Ban" size={24} color="var(--color-destructive)" />
              Suspend Gym Location
            </h2>
            <button
              onClick={onClose}
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              <Icon name="X" size={24} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Tenant Info */}
          <div className="mb-6 p-4 bg-muted/30 rounded-lg">
            <div className="flex items-center gap-3">
              {tenant?.logo_url ? (
                <img src={tenant?.logo_url} alt={tenant?.name} className="w-12 h-12 rounded-lg object-cover" />
              ) : (
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Icon name="Building2" size={24} color="var(--color-primary)" />
                </div>
              )}
              <div>
                <h3 className="font-semibold text-foreground">{tenant?.name}</h3>
                <p className="text-sm text-muted-foreground">{tenant?.email}</p>
              </div>
            </div>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-destructive/10 border border-destructive rounded-lg flex items-center gap-2">
              <Icon name="AlertCircle" size={16} color="var(--color-destructive)" />
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}

          {/* Suspension Reason */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-foreground mb-2">
              Suspension Reason *
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {suspensionReasons?.map((reason) => (
                <button
                  key={reason?.value}
                  onClick={() => setSuspensionReason(reason?.value)}
                  className={`p-3 border rounded-lg flex items-center gap-3 transition-all ${
                    suspensionReason === reason?.value
                      ? 'border-primary bg-primary/10 text-primary' :'border-border bg-card text-foreground hover:border-primary/50'
                  }`}
                >
                  <Icon name={reason?.icon} size={20} />
                  <span className="text-sm font-medium">{reason?.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Suspension Notes */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-foreground mb-2">
              Suspension Notes *
            </label>
            <textarea
              value={suspensionNotes}
              onChange={(e) => setSuspensionNotes(e?.target?.value)}
              placeholder="Provide detailed reason for suspension..."
              rows={4}
              className="w-full px-3 py-2 border border-border rounded-lg bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none"
            />
          </div>

          {/* Notify Members */}
          <div className="mb-6">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={notifyMembers}
                onChange={(e) => setNotifyMembers(e?.target?.checked)}
                className="w-4 h-4 text-primary border-border rounded focus:ring-2 focus:ring-primary"
              />
              <span className="text-sm text-foreground">Notify all gym members about suspension</span>
            </label>
          </div>

          {/* Warning */}
          <div className="mb-6 p-4 bg-warning/10 border border-warning rounded-lg">
            <div className="flex items-start gap-3">
              <Icon name="AlertTriangle" size={20} color="var(--color-warning)" />
              <div>
                <h4 className="font-semibold text-warning mb-1">Warning</h4>
                <p className="text-sm text-warning">
                  Suspending this gym location will immediately disable access for all members and staff. 
                  The gym owner will be notified and can contact support to resolve the issue.
                </p>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <Button
              variant="outline"
              fullWidth
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              fullWidth
              onClick={handleSuspend}
              disabled={loading}
            >
              {loading ? (
                <>
                  <Icon name="Loader2" size={16} className="animate-spin" />
                  Suspending...
                </>
              ) : (
                <>
                  <Icon name="Ban" size={16} />
                  Suspend Location
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuspensionControlPanel;